import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import {
  WebsiteSummary,
  WebsiteTranslationEntry,
  WebsiteTranslationsState
} from '../../models/website-management.models';
import { WebsiteManagementApiError, WebsiteManagementService } from '../../services/website-management.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';

interface EditableTranslationEntry extends WebsiteTranslationEntry {
  isDeleted?: boolean;
}

@Component({
  selector: 'app-website-translations-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './website-translations-page.component.html',
  styleUrls: ['./website-translations-page.component.scss']
})
export class WebsiteTranslationsPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  isWebsitesLoading = true;
  isTranslationsLoading = false;
  isSaving = false;
  loadError = '';
  saveMessage = '';
  newLanguage = '';
  newKey = '';

  websites: WebsiteSummary[] = [];
  selectedWebsitePublicId: string | null = null;
  languages: string[] = ['en', 'ro'];
  entries: EditableTranslationEntry[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly websiteManagementService: WebsiteManagementService,
    private readonly foregroundRefresh: ForegroundRefreshService
  ) {}

  ngOnInit(): void {
    this.loadWebsites();
    this.foregroundRefresh.watch()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshInBackground());
  }

  private loadWebsites(background = false): void {
    if (this.isSaving) return;
    this.websiteManagementService.getWebsites()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (websites) => {
          this.runInAngular(() => {
            this.websites = websites;
            this.isWebsitesLoading = false;
            const queryWebsite = this.route.snapshot.queryParamMap.get('website');
            const selected = queryWebsite && websites.some((website) => website.publicId === queryWebsite)
              ? queryWebsite
              : (this.selectedWebsitePublicId && websites.some((website) => website.publicId === this.selectedWebsitePublicId)
                ? this.selectedWebsitePublicId
                : (websites[0]?.publicId ?? null));
            if (selected && (!background || selected !== this.selectedWebsitePublicId || this.entries.length === 0)) {
              this.selectWebsite(selected, true);
            }
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isWebsitesLoading = false;
            if (!background) this.loadError = error.message;
          });
        }
      });
  }

  get selectedWebsite(): WebsiteSummary | null {
    return this.websites.find((website) => website.publicId === this.selectedWebsitePublicId) ?? null;
  }

  selectWebsite(websitePublicId: string, replaceUrl = false): void {
    if (this.selectedWebsitePublicId === websitePublicId && this.entries.length > 0) {
      return;
    }

    this.selectedWebsitePublicId = websitePublicId;
    this.saveMessage = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { website: websitePublicId },
      queryParamsHandling: 'merge',
      replaceUrl
    });
    this.loadTranslations(websitePublicId);
  }

  loadTranslations(websitePublicId = this.selectedWebsitePublicId, background = false): void {
    if (!websitePublicId) {
      return;
    }

    if (!background) {
      this.isTranslationsLoading = true;
      this.loadError = '';
    }
    this.websiteManagementService.getTranslations(websitePublicId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => this.runInAngular(() => this.applyState(state)),
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isTranslationsLoading = false;
            if (!background) this.loadError = error.message;
          });
        }
      });
  }

  private refreshInBackground(): void {
    if (this.isSaving) return;
    this.loadWebsites(true);
    if (this.selectedWebsitePublicId) {
      this.loadTranslations(this.selectedWebsitePublicId, true);
    }
  }

  addLanguage(): void {
    const language = this.normalizeLanguage(this.newLanguage);
    if (!language) {
      this.saveMessage = 'Language code must look like en, ro, de, or en-us.';
      return;
    }
    if (this.languages.includes(language)) {
      this.newLanguage = '';
      return;
    }

    this.languages = [...this.languages, language];
    this.entries = this.entries.map((entry) => ({
      ...entry,
      translations: {
        ...entry.translations,
        [language]: ''
      }
    }));
    this.newLanguage = '';
  }

  removeLanguage(language: string): void {
    if (this.languages.length <= 1) {
      this.saveMessage = 'At least one language column is required.';
      return;
    }

    this.languages = this.languages.filter((entry) => entry !== language);
    this.entries = this.entries.map((entry) => {
      const translations = { ...entry.translations };
      delete translations[language];
      return { ...entry, translations };
    });
  }

  addEntry(): void {
    const key = this.normalizeKey(this.newKey);
    if (!key) {
      this.saveMessage = 'Translation key must use letters, numbers, dots, underscores, or dashes.';
      return;
    }
    if (this.entries.some((entry) => this.normalizeKey(entry.key) === key)) {
      this.saveMessage = `Translation key "${key}" already exists.`;
      return;
    }

    const translations = this.languages.reduce<Record<string, string>>((accumulator, language) => {
      accumulator[language] = '';
      return accumulator;
    }, {});
    this.entries = [
      ...this.entries,
      {
        publicId: '',
        key,
        translations,
        notes: null,
        createdAt: '',
        updatedAt: ''
      }
    ];
    this.newKey = '';
  }

  removeEntry(index: number): void {
    this.entries = this.entries.filter((_, entryIndex) => entryIndex !== index);
  }

  saveTranslations(): void {
    if (!this.selectedWebsitePublicId || this.isSaving) {
      return;
    }

    const validationErrors = this.validate();
    if (validationErrors.length > 0) {
      this.saveMessage = validationErrors[0];
      return;
    }

    this.isSaving = true;
    this.saveMessage = '';
    this.websiteManagementService.updateTranslations(this.selectedWebsitePublicId, {
      languages: this.languages,
      entries: this.entries.map((entry) => ({
        publicId: entry.publicId || null,
        key: this.normalizeKey(entry.key),
        translations: this.languages.reduce<Record<string, string>>((accumulator, language) => {
          accumulator[language] = entry.translations[language] ?? '';
          return accumulator;
        }, {}),
        notes: entry.notes?.trim() || null
      }))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runInAngular(() => {
            this.applyState(state);
            this.isSaving = false;
            this.saveMessage = 'Translations saved. Publish the website again to include them in the live deployment.';
          });
        },
        error: (error: WebsiteManagementApiError) => {
          this.runInAngular(() => {
            this.isSaving = false;
            this.saveMessage = error.message;
          });
        }
      });
  }

  trackWebsite(_: number, website: WebsiteSummary): string {
    return website.publicId;
  }

  trackLanguage(_: number, language: string): string {
    return language;
  }

  trackEntry(index: number, entry: EditableTranslationEntry): string {
    return entry.publicId || `${entry.key}-${index}`;
  }

  private applyState(state: WebsiteTranslationsState): void {
    this.languages = state.languages.length > 0 ? state.languages : ['en'];
    this.entries = state.entries.map((entry) => ({
      ...entry,
      translations: this.languages.reduce<Record<string, string>>((accumulator, language) => {
        accumulator[language] = entry.translations[language] ?? '';
        return accumulator;
      }, {})
    }));
    this.isTranslationsLoading = false;
    this.loadError = '';
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }

  private validate(): string[] {
    const languageErrors = this.languages
      .map((language) => this.normalizeLanguage(language))
      .filter((language) => !language);
    if (languageErrors.length > 0) {
      return ['All language columns must use valid language codes.'];
    }

    const keys = this.entries.map((entry) => this.normalizeKey(entry.key));
    if (keys.some((key) => !key)) {
      return ['Every translation row needs a valid key.'];
    }
    if (new Set(keys).size !== keys.length) {
      return ['Translation keys must be unique.'];
    }

    return [];
  }

  private normalizeLanguage(value: string): string {
    const normalized = value.trim().toLowerCase();
    return /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/.test(normalized) ? normalized : '';
  }

  private normalizeKey(value: string): string {
    const normalized = value.trim().toLowerCase();
    return /^[a-z0-9][a-z0-9._-]*$/.test(normalized) ? normalized : '';
  }
}
